import ParameterContext from 'effects/ParameterContext.js';
import {INSTRUMENT_TYPE_TO_NAME} from 'constants/instrument-types.js';
import {chopByPatterns} from './chopByPatterns.js';
import {getPatternLength} from 'engine/BouncingEngine.js';
import {sleep} from 'utils/sleep.js';
import SoundUploader from 'api/SoundUploader.js';
import {crop} from 'engine/Crop.js';
import {bounceTrackPatterns} from 'engine/BouncingEngine.js';
import {
    setPulledEffects,
    deleteAutomationsForParameters} from 'actions/index.js';
import FreezeColumn from './FreezeColumn.js';
import {INSTRUMENT, INSTRUMENT_RACK} from 'constants/track-types.js';
import Sample from 'repos/Sample.js';
import Tone from 'tone';
import MidiTrigger from 'effects/midi/MidiTrigger.js';
import {toggleEffect, addEffect} from 'operations/actions.js';
import WarpEffect from 'effects/midi/WarpEffect.js';
import Sampler from 'instruments/Sampler.js';
import StepData from 'pattern/StepData.js';
import EffectsChain from 'effects/EffectsChain.js';
import {
    setSample,
    setTrackIsLoading, 
    setViMode,
    setEffectParameterValues,
    setAllTrackSteps,
    setShowTrackEffects,
    setEffectsChainToShow,
    setLoadingText,
    setTrackBrowsedSamples,
    setTrackSteps,
    setTrackPianoRollSteps,
    setTrackFills,
    setTrackVolume,
    setTrackPanning,
    setTrackSendVolume,
    setTrackMuted,
    setTrackSolo,
    setSampleAuditioning,
    setTrackIsEnabled,
    setTrackPatternLocked,
    setTrackPatternLocks,
    setTrackFrozen,
    setFrozenTrackPlaying,
       } from 'actions/index.js';
const TIME_16N = new Tone.Time('16n');

export default superclass => class extends superclass  {
    frozenSamplesToSave = [];

    get frozenColumn() {
        // should in the future checkk which one is selected
        return this.frozenColumns[0];
    }

    newFrozenColumn() {
        if (this.frozenColumns === undefined) {
            let col = new FreezeColumn(this.sequencerEngine, this, 0);
            col.output.connect(this.gainNode);
            this.frozenColumns = [col];
            return col;
        }
        let col = new FreezeColumn(this.sequencerEngine, this, this.frozenColumns.length);
        col.output.connect(this.gainNode);
        this.frozenColumns = [... this.frozenColumns, col];
        return col;
    }

    freeze(samples, skipSaveSettings=false) {
        if (!Array.isArray(samples)) {
            samples = [samples];
        }

        // first look for all pullable parts and unnpull

        if (!this.isAudioTrack) {
            let pulled = this.state.effects.pulledEffects.filter(
                x => (!x.context || x.context.track !== this) && x.track !== this);
            this.dispatch(setPulledEffects(pulled));
            let mapped = this.getAllParameters()
                .forEach(p => p.isMapped && p.isMapped.unmap(p));
        }
        
        // get track preset json (includes effects/sample/etc)
        let oldSample = this.sample;
        let frozenColumn = this.isAudioTrack ? this.newFrozenColumn() : this.frozenColumns[0] || this.newFrozenColumn();

        if (oldSample) {
            samples.forEach(sample => sample.name = oldSample.name);
            samples.forEach(sample => sample.tags = sample.tags.concat(oldSample.tags));
            samples.forEach(sample => sample.videoId = oldSample.videoId);
            samples.forEach(sample => sample.sourceSample = oldSample);
        }

        this.tagFrozenSamples(samples);

        this.frozenSamplesToSave = [... this.frozenSamplesToSave, ... samples];

        if (!this.isFrozen && !skipSaveSettings) {
            this.lastFrozenSettings = this.sequencerEngine.getTrackPreset(this.trackNumber, false);
        }
        frozenColumn.frozenSettings.push(this.isAudioTrack ? {} : this.lastFrozenSettings);

        let isFrozen = this.isFrozen;
        this.store.dispatch(setTrackFrozen(this.subSequence.id, this.id, true));

        if (!this.isAudioTrack) {
            this.clearEffectsAndSamplers();
            this.effectsChains[0].initDefault();
            this.applyWarp();
        }
        
        samples.forEach(
            sample => 
                frozenColumn.loadFrozenSample(sample, undefined));

        if (this.getTrackType() === INSTRUMENT && !isFrozen) {
            this.instrument.dispose();
        } else if (this.getTrackType() === INSTRUMENT_RACK && !isFrozen) {
            this.tracks.forEach(
                ({track}) => track.instrument && track.instrument.dispose());
        }

        frozenColumn.playing = frozenColumn.frozenSamples.length - 1;
        // this.pattern.frozenPatternPlaying = this.frozenSamplers.length - 1;
        this.gated = false;
        this.cachedPlayhead = undefined;
        this.parametersToScale = undefined;

        this.sequencerEngine.selectTrack(0);
        this.sequencerEngine.selectTrack(this.trackNumber);

        if (this.isEnabled && !this.isAudioTrack) {
            this.setIsEnabled(false);
        }

        this.skipMidiMasterParameter.set(true);
        this.trackIsLoading = false;
    }

    clearEffectsAndSamplers(mapped=[]) {
        this.volumeParameter.deleteAutomations();
        this.volumeParameter.deleteLocks();
        this.effectsChains.forEach(e => e.clear());
        this.midiEffects.effects = [];
        this.liveEffects.effects = [];

        if (this.tracks) {
            this.tracks.forEach(
                ({track}) => {
                    track.effectsChains.forEach(e => e.clear());
                    track.midiEffects.effects = [];
                    track.liveEffects.effects = [];
                });
        }

        let parameters = this.getAllParameters();
        let automationParameters = parameters
            .filter(x => x.automations && x.automations.length > 0);
        this.sequencerEngine.store.dispatch(
            deleteAutomationsForParameters(automationParameters));
    }

    triggerFrozenSample(time) {
        let stepData = new StepData();
        stepData.loadDefaults();
        this.midiEffects.input(stepData, time, 0);
    }

    get isFrozen() {
        let frozen = this.store.getState().tracks.tracksFrozen[this.subSequence.id] || {};
        return frozen[this.id];
    }

    set isFrozen(isFrozen) {                                 
        this.store.dispatch(setTrackFrozen(
            this.subSequence.id,
            this.id,
            isFrozen));
    }

    async unfreeze() {
        this.cachedPlayhead = undefined;
        let frozenJson = this.lastFrozenSettings;
        this.clearEffectsAndSamplers();
        this.store.dispatch(setTrackFrozen(this.subSequence.id, this.id, false));
        await this.unfreezeInstrument(frozenJson);

        // todo make this track data init a reuseauble func
        this.trackData = new StepData(
            this.sequencerEngine,
            this.mix,
            new ParameterContext().forTrack(this));
        this.trackData.loadDefaults();

        if (frozenJson.trackParameters) {
            this.gated = frozenJson.trackParameters.gated;
        }

        this.trackData.loadFromJson(
            frozenJson.trackData,
            undefined,
            false,
            false,
            this.sequencerEngine);

        this.effectsChains[0].dispose();
        
        await this.sequencerEngine.loadEffectsPreset(
            frozenJson,
            false,
            this);

        if (this.tracks) {
            let i=0;
            for (let {track} of this.tracks) {
                await this.sequencerEngine.loadEffectsPreset(
                    frozenJson.trackParameters.tracks[i],
                    false,
                    track);
                i++;
            }
        }

        this.skipMidiMasterParameter.set(false);

        this.frozenColumns.forEach(
            col => col.frozenSamplers.forEach(
                s => s.release()));

        this.frozenColumns.forEach(
            col => col.playing = undefined);

        setTimeout(() => {
        // check if any shits need to be loaded
            this.loadLostParameters();
        }, 750);
 
    }

    unfreezeInstrument(json=this.frozenJson) {
        return;
        this.sequencerEngine.loadSamplersIntoTrack(
            this,
            json.trackParameters,
            this.sequencerEngine.tracks.length,
            this.sequencerEngine.currentSubSequence);
    }

    getFreezeJson() {
        let json = {
            lastFrozenSettings: this.lastFrozenSettings,
            frozenColumns: this.frozenColumns.map(x => x.getJson())
        };
        return json;
    }

    getFreezeJsonSync() {
        let json = {
            lastFrozenSettings: this.lastFrozenSettings,
            frozenColumns: this.frozenColumns.map(x => x.getJsonSync())
        };
        return json;
    }

    loadFreezeJsonSync(json) {
        // load the samples
        this.lastFrozenSettings = json.lastFrozenSettings;

        for (let i in json.frozenColumns) {
            let column = this.frozenColumns[parseInt(i)] || this.newFrozenColumn();
            column.fromJsonSync(json.frozenColumns[i]);
       }
    }

    async loadFreezeJson(json) {
        // load the samples
        this.lastFrozenSettings = json.lastFrozenSettings;

        for (let i in json.frozenColumns) {
            let column = this.frozenColumns[parseInt(i)] || this.newFrozenColumn();
            await column.fromJson(json.frozenColumns[i]);
       }
    }

    applyWarp() {
        this.operationManager.dispatch(
            addEffect(new WarpEffect(this), this));
    }

    dragFreezeIntoColumn(sampleId, column=this.newFrozenColumn()) {
        // get the sample/sampler/settings
        let sampler = undefined;
        let sample = undefined;
        let settings = undefined;

        for (let col of this.frozenColumns) {
            for (let i in col.frozenSamples) {
                if (col.frozenSamples[i].ipfsHash === sampleId) {
                    sample = col.frozenSamples[i];
                    sampler = col.frozenSamplers[i];
                    settings = col.frozenSettings[i];

                    // delete it from this column
                    col.frozenSamples.splice(i, 1);
                    col.frozenSettings.splice(i, 1);
                    col.frozenSamplers.splice(i, 1);
                    col.updateRedux();
                    break;
                }
            }
        }

        if (sampler === undefined) {
            return;
        }

        sampler.gainNode.disconnect();
        column.frozenSamplers.push(sampler);
        column.frozenSamples.push(sample);
        column.frozenSettings.push(settings);
        sampler.gainNode.connect(column.output);
        column.playing = 0;
        column.updateRedux();
    }

    disposeFreezes() {
        this.frozenColumns.forEach(c => c.dispose());
        this.frozenColumns = undefined;
    }

    async freezeAllPatterns() {
        this.store.dispatch(setLoadingText("Freezing track\'s patterns"));
        await sleep(250);
        this.sequencerEngine.stop();
        let audioBuffer = await bounceTrackPatterns(this.sequencerEngine, this);
        let bounces = await chopByPatterns(this.sequencerEngine, audioBuffer, this.patterns.length, [this]);
        this.freeze(bounces);
        // need to bounce and split

        let loops = this.sequencerEngine.state.freeze.loops;
        let len = getPatternLength(this.sequencerEngine, 0, this.sequencerEngine.tracks) * loops;

        for (let i in this.patterns) {
            i = parseInt(i);
            this.switchToPattern(i);
            let lenRatio = (len) / this.pattern.length();
            for (let j=0; j < Math.log2(lenRatio); j++) {
                this.pattern.doubleTrackLength();
            }
            this.frozenColumns[0].playing = i;
            this.store.dispatch(setLoadingText(undefined));
        }
    }

    tagFrozenSamples(samples) {
        if (this.instrument) {
            let instrumentName = INSTRUMENT_TYPE_TO_NAME[this.instrument.getInstrumentType()];
            samples.forEach(s => s.tags.push(instrumentName));
            if (this.presetName) {
                samples.forEach(s => s.tags.push(this.presetName));
            }
        }
    }

}
